import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { Box, styled, Typography } from "@mui/material"
import { createPath, ROUTE } from "app-constants/routing"
import SearchBar from "components/SearchBar"
import { ActivityIndicator } from "components/activity-indicator"
import { NoResultsFoundFallback } from "components/common/NoResultsFoundFallback"
import { SortByDropdown } from "components/common/SortByDropdown"
import { ExhibitorCardContainer } from "components/exhibitors/ExhibitorCardContainer"
import { ExhibitorsPageName } from "components/exhibitors/ExhibitorsPageName"
import { ButtonIconLink } from "components/navigation/ButtonIconLink"
import { useExhibitorCopyText } from "hooks/exhibitors/useExhibitorsCopyText"
import { useFavoriteExhibitors } from "hooks/exhibitors/useFavoriteExhibitors"
import { useExhibitorChat } from "hooks/useExhibitorChat"
import { useOnElementVisible } from "hooks/useOnElementVisible"
import { useSearchValue } from "hooks/useSearchValue"
import { useSortBy } from "hooks/useSortBy"
import { Filter } from "models/convention-api/types"
import ConventionProviderExhibitor from "models/convention-api/v1/ConventionProviderExhibitor"
import ConventionProviderExhibitorCount from "models/convention-api/v1/ConventionProviderExhibitorCount"
import React from "react"

const _Container = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",

  width: "100%",

  paddingTop: 20,
  paddingBottom: 20,

  [theme.breakpoints.up(720)]: {
    paddingTop: 30,
    paddingBottom: 30,
  },
}))

const _HeaderContainer = styled(Box)({
  display: "grid",
  gridGap: 30,
  gridTemplateColumns: "1fr 1fr",
  gridTemplateAreas: `
    'pageName backToExplore' 
    'searchBar sortDropdown' 
  `,

  width: "100%",
  marginBottom: 16,
})

const _PageNameContainer = styled(Box)(({ theme }) => ({
  gridArea: "pageName",
  [theme.breakpoints.up("sm")]: {
    display: "flex",
    alignItems: "center",
  },
}))

const _BackToExploreButtonContainer = styled(Box)(({ theme }) => ({
  gridArea: "backToExplore",
  display: "flex",
  alignItems: "center",

  [theme.breakpoints.up("sm")]: {
    justifyContent: "flex-end",
  },
}))

const _SearchBarContainer = styled(Box)(({ theme }) => ({
  gridArea: "searchBar",
  display: "flex",
  justifyContent: "stretch",

  [theme.breakpoints.up("sm")]: {
    justifyContent: "flex-start",
  },
}))

const _SearchBar = styled(SearchBar)(({ theme }) => ({
  width: "100%",
  borderRadius: 40,
  "& > .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.gray.dark,
  },

  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
})) as typeof SearchBar

const _SortDropdownContainer = styled(Box)({
  gridArea: "sortDropdown",
  display: "flex",
  justifyContent: "flex-end",
})

const _ResultsContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(2.6),
  marginBottom: theme.spacing(2),
}))

const _ResultsTypography = styled(Typography)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(2.6),
  marginBottom: theme.spacing(2),
}))

const _ExhibitorsContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  justifyContent: "center",
  gridGap: theme.spacing(1.8),
  gridTemplateColumns: "1fr",

  width: "100%",

  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  },
}))

const _LoadingIndicatorContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: theme.spacing(2),
}))

interface ExhibitorsBrowseAllProps {
  conventionIds?: string[] | string
  exhibitorCardHref: string
  fallbackDataExhibitors?: ConventionProviderExhibitor[][]
  fallbackDataExhibitorsCount?: ConventionProviderExhibitorCount
  filter?: Filter
  showHeader?: boolean
  withBackToExplore?: boolean
}

export const ExhibitorsBrowseAll = (props: ExhibitorsBrowseAllProps) => {
  const {
    conventionIds = [],
    fallbackDataExhibitorsCount,
    fallbackDataExhibitors,
    exhibitorCardHref,
    withBackToExplore = false,
    filter = {},
    showHeader = true,
  } = props

  const exhibitorsCopyText = useExhibitorCopyText()
  const sortBy = useSortBy("institution.name")
  const searchValue = useSearchValue()

  const exhibitorChat = useExhibitorChat()
  const favoriteExhibitors = useFavoriteExhibitors()
  const exhibitorsCountModel = ConventionProviderExhibitorCount.useOne({
    filter: { convention_id: conventionIds, q: searchValue.query, disabled: "false", ...filter },
    fallbackData: searchValue.query.length ? undefined : fallbackDataExhibitorsCount,
  })

  const exhibitors = ConventionProviderExhibitor.useMore({
    limit: 20,
    // Default is ascending (a-z)
    sort: sortBy.sortValue,
    filter: { convention_id: conventionIds, q: searchValue.query, disabled: "false", ...filter },
    includes: ["institution", "institution.chatAvailable"],
    fallbackData: searchValue.query.length ? undefined : fallbackDataExhibitors,
  })

  const loadingIndicatorContainerRef = useOnElementVisible(() => {
    if (exhibitors.isLoading) return
    if (!exhibitors.hasMore) return

    exhibitors.loadMore()
  }, [exhibitors.isLoading, exhibitors.hasMore])

  const shouldShowLoadingIndicator =
    !exhibitors.data || !exhibitorsCountModel.data || !favoriteExhibitors.isReady

  const shouldShowNoResultsFoundFallback =
    !shouldShowLoadingIndicator && exhibitors.data?.length === 0 && !exhibitors.isLoading
  const shouldShowFoundResults =
    !shouldShowLoadingIndicator && !shouldShowNoResultsFoundFallback && favoriteExhibitors.isReady

  const exhibitorsCount = exhibitorsCountModel.data?.count ?? 0
  return (
    <_Container>
      <_HeaderContainer>
        {showHeader && (
          <_PageNameContainer>
            <ExhibitorsPageName />
          </_PageNameContainer>
        )}
        {withBackToExplore && (
          <_BackToExploreButtonContainer>
            <ButtonIconLink
              ariaLabel="Back to explore page"
              label="Back To Explore"
              href={createPath({ path: ROUTE.EXHIBITORS })}
              startIcon={<ArrowBackIcon />}
            />
          </_BackToExploreButtonContainer>
        )}
        <_SearchBarContainer>
          <_SearchBar
            variant="outlined"
            placeholder={`Search ${exhibitorsCopyText?.plural}`}
            onChange={searchValue.setValue}
            value={searchValue.value}
            onSearch={searchValue.performSearch}
            onEnter={searchValue.performSearch}
            onClear={searchValue.cancelSearch}
            iconType={searchValue.query.length === 0 ? "search" : "clear"}
          />
        </_SearchBarContainer>
        <_SortDropdownContainer>
          <SortByDropdown
            popoverId="exhibitors-sort-by-dropdown"
            value={sortBy.value}
            onChange={sortBy.setValue}
          />
        </_SortDropdownContainer>
      </_HeaderContainer>
      {shouldShowNoResultsFoundFallback && <NoResultsFoundFallback />}
      {shouldShowLoadingIndicator && (
        <_LoadingIndicatorContainer>
          <ActivityIndicator />
        </_LoadingIndicatorContainer>
      )}
      {shouldShowFoundResults && (
        <>
          <_ResultsContainer>
            <_ResultsTypography>
              {exhibitorsCount} {exhibitorsCopyText?.byCount(exhibitorsCount)}
            </_ResultsTypography>
          </_ResultsContainer>
          <_ExhibitorsContainer>
            {exhibitors.data?.map((exhibitor) => (
              <ExhibitorCardContainer
                key={exhibitor.id}
                exhibitor={exhibitor}
                href={`${exhibitorCardHref}/${exhibitor.id}`}
                exhibitorChat={exhibitorChat}
                favoriteExhibitors={favoriteExhibitors}
              />
            ))}
          </_ExhibitorsContainer>
          {(exhibitors.hasMore || exhibitors.isLoading) && (
            <_LoadingIndicatorContainer {...{ ref: loadingIndicatorContainerRef }}>
              <ActivityIndicator />
            </_LoadingIndicatorContainer>
          )}
        </>
      )}
    </_Container>
  )
}
